import { type AxiosResponse } from 'axios'
import i18n from '@/plugins/i18n'

/**
 * Erros da API Corp (não usam envelope { data, meta }): JSON plano com message, status e errors[].
 * Lógica de UI deve usar HTTP status + errors[].code (identificador estável). Ver docs backend
 * "02. Estrutura de Erros" e "12. Erros — Manager, Sessions e Business".
 */

/** Chaves i18n para mensagens de fallback do client (`corp.api_errors.*` em pt-BR.json). */
export const CORP_API_ERROR_I18N_KEYS = {
  generic: 'corp.api_errors.generic',
  network: 'corp.api_errors.network',
} as const

function corpApiErrorFallbackMessage(key: keyof typeof CORP_API_ERROR_I18N_KEYS): string {
  return i18n.global.t(CORP_API_ERROR_I18N_KEYS[key])
}

const ERROR_STATUS = 500;
const ERROR_STATUS_RANGE = [400, 499];

export interface ApiError {
  status: number
  code: string
  title: string
  field?: string
  type?: string
}

export interface ApiErrorResponse {
  message: string
  status: number
  errors: ApiError[]
}

export class ApiErrorException extends Error {
  private readonly _status: number
  private readonly _errors: ApiError[]

  constructor(payload: Partial<ApiErrorResponse> & { message: string }) {
    super(payload.message)
    this._status = payload.status ?? 0
    this._errors = payload.errors ?? []
    Object.setPrototypeOf(this, ApiErrorException.prototype)
  }

  getMessage(): string {
    return this.message
  }

  getStatus(): number {
    return this._status
  }

  getErrors(): ApiError[] {
    return this._errors
  }

  isValidationError(): boolean {
    return this._status === 422
  }

  getFieldErrors(): Record<string, string[]> {
    const acc: Record<string, string[]> = {}
    for (const err of this._errors) {
      const field = err.field ?? 'base'
      if (!acc[field]) acc[field] = []
      acc[field].push(err.title)
    }
    return acc
  }

  /** Primeiro errors[].code (padrão Corp). */
  getPrimaryErrorCode(): string | undefined {
    return this._errors[0]?.code
  }
}

/**
 * Corpo de erro da API Corp (doc): `message`, `status`, `errors[]` no topo — sem `data`/`meta`.
 * Objeto com `data` é tratado como envelope de sucesso e não como erro (evita misturar formatos).
 */
function normalizeErrorBody(data: unknown): ApiErrorResponse | null {
  if (!data || typeof data !== 'object') return null
  const o = data as Record<string, unknown>
  if ('data' in o) return null
  if (typeof o.message !== 'string') return null
  if (!Array.isArray(o.errors)) return null
  return {
    message: o.message,
    status: typeof o.status === 'number' ? o.status : 0,
    errors: o.errors as ApiError[],
  }
}

/** Primeiro `errors[].code` do corpo de erro oficial (`message` + `errors[]` no topo). */
export function getPrimaryApiErrorCode(responseBody: unknown): string | undefined {
  const parsed = normalizeErrorBody(responseBody)
  const c = parsed?.errors?.[0]?.code
  return typeof c === 'string' && c.length > 0 ? c : undefined
}

/** `message` do corpo de erro oficial. */
export function getCorpApiErrorMessage(responseBody: unknown): string | undefined {
  const parsed = normalizeErrorBody(responseBody)
  return parsed?.message
}

/** 403 Manager: apenas termos não aceitos → fluxo de aceite (onboarding). `authorization_error` não entra aqui. */
export function isCorpManagerTermsFlowCode(code: string | undefined): boolean {
  return code === 'terms_not_accepted'
}

/**
 * Converte erro Axios (`error.response`) em ApiErrorException.
 * Usado no interceptor da base. Erro de rede → mensagem genérica; corpo fora do padrão → genérica.
 */
export function handleError(response: AxiosResponse | undefined): never {
  if (!response) {
    throw new ApiErrorException({
      message: corpApiErrorFallbackMessage('network'),
      status: 0,
      errors: [],
    })
  }
  const body = normalizeErrorBody(response.data)
  if (!body) {
    throw new ApiErrorException({
      message: corpApiErrorFallbackMessage('generic'),
      status: response.status,
      errors: [],
    })
  }
  if (body.status >= ERROR_STATUS_RANGE[0] && body.status <= ERROR_STATUS_RANGE[1]) {
    throw new ApiErrorException({
      message: body.message as string,
      status: body.status as number,
      errors: body.errors as ApiError[],
    })
  }
  if (body.status >= ERROR_STATUS) {
    throw new ApiErrorException({
      message: corpApiErrorFallbackMessage('generic'),
      status: response.status,
      errors: [],
    })
  }
  throw new ApiErrorException({
    message: (body?.message as string) ?? `HTTP ${response.status}`,
    status: response.status,
    errors: [],
  })
}
